import React, { useEffect, useState, useCallback } from 'react';
import rest from 'util/Api';
import JsDownload from 'js-file-download';
import { useSelector, useDispatch } from 'react-redux';
import usePackagesWithPaquers from 'redux/packagesWithPaquers';
import usePaquers from 'redux/paquers';
import usePaquerypoints from 'redux/paquerypoint';
import { actions } from 'redux/packagesWithPaquers/slice';
import { actions as paquerActions } from 'redux/paquers/slice';
import { actions as pqpointActions } from 'redux/paquerypoint/slice';
import { paquersItemsSelector } from 'redux/paquers/selectors';
import { selectGlobals } from 'redux/globals/selectors';
import { paquerypointsItemsSelector } from 'redux/paquerypoint/selectors';
import {
  packagesWithPaquersSearchSelector,
  packagesWithPaquersStatusSelector,
  packagesWithPaquersPaquerSelector,
  packagesWithPaquersLoadedSelector,
  packagesMarketFilterSelector,
  packagesWithPaquersDateSelector,
  packagesWithPaquersItemsSelector,
  packagesWithPaquersPageableSelector,
} from 'redux/packagesWithPaquers/selectors';
import { notification } from 'antd';
import moment from 'moment';
import PaqueryTable from '@paquery-team/lib-table';
import {
  WebStyle,
  TableDateTimeFormat,
  API,
  ALL_STATUS_FILTERING,
} from 'default';
import externalCodeColumn from 'components/tables/columns';
import useMarketplaces from 'redux/marketplaces';
import ViewPackageButton from 'components/viewPackageButton';

const destinationPersonNameColumn = {
  title: 'Nombre destinatario',
  dataIndex: 'destinationPersonName',
};

const destinationAddressColumn = {
  title: 'Destino',
  dataIndex: 'destinationAddress',
};

const deliveryTermColumn = {
  title: 'Plazo',
  dataIndex: 'deliveryTermDescription',
};

const arrivedAtPaqueryPointDateColumn = {
  title: 'Arribó a PaqueryPoint',
  dataIndex: 'arrivedAtPaqueryPointDate',
  align: 'center',
};

const statusColumn = {
  title: 'Estado',
  dataIndex: 'statusDescription',
};

const paquerypointColumn = {
  title: 'PaqueryPoint',
  dataIndex: 'paquerypoint',
  align: 'center',
};

const paquerColumn = {
  title: 'Paquer',
  dataIndex: 'paquer',
  align: 'center',
};

const sizeColumn = {
  title: 'Tamaño',
  dataIndex: 'packageSizeDescription',
  align: 'center',
  render: (text) => (
    <span
      style={{
        background: WebStyle.paqueryGreen,
        padding: 3,
        color: WebStyle.paqueryFontColor,
        fontWeight: 'bold',
      }}
    >
      {text[0]}
    </span>
  ),
};

const viewPackageColumn = {
  align: 'center',
  render: (_, record) => <ViewPackageButton packet={record} />,
};

const dataColumns = [
  externalCodeColumn,
  destinationPersonNameColumn,
  destinationAddressColumn,
  deliveryTermColumn,
  sizeColumn,
  arrivedAtPaqueryPointDateColumn,
  paquerypointColumn,
  statusColumn,
  paquerColumn,
];

const columnsLargeDevice = [...dataColumns, viewPackageColumn];

const columnsSmallDevice = [externalCodeColumn, viewPackageColumn];

const columnsMediumDevice = [
  externalCodeColumn,
  statusColumn,
  viewPackageColumn,
];

const PackageWithPaquers = () => {
  usePackagesWithPaquers();
  usePaquers();
  usePaquerypoints();
  useMarketplaces();
  const dispatch = useDispatch();
  const globals = useSelector(selectGlobals);
  const pageable = useSelector(packagesWithPaquersPageableSelector);
  const items = useSelector(packagesWithPaquersItemsSelector);
  const paquers = useSelector(paquersItemsSelector);
  const paquerypoints = useSelector(paquerypointsItemsSelector);
  const search = useSelector(packagesWithPaquersSearchSelector);
  const status = useSelector(packagesWithPaquersStatusSelector);
  const date = useSelector(packagesWithPaquersDateSelector);
  const marketFilter = useSelector(packagesMarketFilterSelector);
  const loaded = useSelector(packagesWithPaquersLoadedSelector);
  const paquerId = useSelector(packagesWithPaquersPaquerSelector);
  const [paquerList, setPaquerList] = useState([]);
  const [disabled, setDisabled] = useState(false);
  useEffect(() => {
    dispatch(paquerActions.initial());
    dispatch(pqpointActions.initial());
  }, [dispatch]);

  useEffect(() => {
    if (paquers.length > 0) {
      setPaquerList(
        paquers.map((paquer) => ({
          value: paquer.id,
          name: `${paquer.name} ${paquer.lastName}`,
        })),
      );
    }
  }, [paquers]);

  const handleExportCsv = async () => {
    setDisabled(true);
    try {
      const searchParams = new URLSearchParams({
        from: date.from,
        to: date.to,
        search,
        status: status === ALL_STATUS_FILTERING ? '' : status,
        marketplaceId: marketFilter || '',
        paquerId,
      });
      const response = await rest.get(
        `${API.reports.packagesbyPaquer}?${searchParams.toString()}`,
        {
          responseType: 'blob',
        },
      );
      const paquerFilteredName = paquerId
        ? `${
            paquerList.find((paquerOnList) => paquerOnList.value === paquerId)
              .name
          }`
        : 'Paquer';
      const filename = `Paquetes-Por-${paquerFilteredName}-${moment()
        .format('DD-MM-YYYY HH_mm_ss')
        .toString()}.xls`;
      JsDownload(response.data, filename);
    } catch (error) {
      notification.error({
        message: 'Ha ocurrido un error al traer los paquetes',
        description:
          'Intente nuevamente por favor o informelo a los administradores',
      });
    } finally {
      setDisabled(false);
    }
  };

  const searchPaquerCallback = useCallback(
    (newPaquerId) => {
      dispatch(actions.updatePaquerId(newPaquerId));
    },
    [dispatch],
  );

  const updatePaginate = useCallback(
    (page) => {
      dispatch(actions.updatePageable(page));
    },
    [dispatch],
  );

  const updateDate = useCallback(
    (value) => {
      dispatch(actions.updateDate(value));
    },
    [dispatch],
  );

  const selectors = [
    {
      onChange: searchPaquerCallback,
      placeholder: 'Por paquer',
      defaultValue: paquerId,
      xsSpan: 24,
      list: paquerList,
      showAll: true,
    },
  ];

  let filteredPackages = [];
  if (loaded) {
    filteredPackages = items.map((packet) => ({
      ...packet,
      key: packet.id,
      externalCode: packet.externalCode,
      destinationPersonName: packet.shippingScheduleDestination.name,
      destinationAddress:
        packet.shippingScheduleDestination.shippingAddress.addressDetail,
      deliveryTermDescription: globals.deliveryTerm.find(
        (term) => term.value === packet.deliveryTerm,
      ).description,
      packageSizeDescription: globals.packages.size.find(
        (packSize) => packSize.value === packet.packageSize,
      ).name,
      arrivedAtPaqueryPointDate: packet.arrivedAtPaqueryPointDate
        ? moment(packet.arrivedAtPaqueryPointDate).format(TableDateTimeFormat)
        : null,
      paquerypoint:
        packet.shippingScheduleDestination.paqueryPointId &&
        paquerypoints.length > 0
          ? paquerypoints.find(
              (ppInList) =>
                ppInList.id ===
                packet.shippingScheduleDestination.paqueryPointId,
            )?.name
          : null,
      statusDescription: globals.packages.status.find(
        (state) => state.value === packet.status,
      ).name,
      paquer: packet.shippingScheduleDestination?.driver
        ? `${packet.shippingScheduleDestination.driver.name} ${packet.shippingScheduleDestination.driver.lastName} (${packet.shippingScheduleDestination.driver.id})`
        : '',
    }));
  }

  return (
    <PaqueryTable
      loading={!loaded}
      header={{
        selectors,
        dateItems: {
          onDateSelection: updateDate,
          required: {
            value: true,
            message: 'La fecha es requerida',
          },
        },
        refresh: () => dispatch(actions.refreshPage()),
        onExportCsv: { callback: handleExportCsv, disabled },
      }}
      usePackageRowColors
      onChangePaginate={updatePaginate}
      dataSource={filteredPackages}
      paginate={pageable}
      dataColumns={dataColumns}
      colsForSmallDevice={columnsSmallDevice}
      colsForMediumDevice={columnsMediumDevice}
      colsForLargeDevice={columnsLargeDevice}
    />
  );
};

export default PackageWithPaquers;
